/**
 * DIV-027 — one round-trip for the page's calculator (#116 item 24).
 *
 * `GET calculator/app/resolve?shop&productId&pageUrl&isPreview&locale` does on the
 * server what three requests did here: the pageurl mapping, the product
 * assignment (with the store's global rule) and the `calculator/app` payload,
 * in the same order with the same rules. Measured before this change (mobile,
 * 2026-09-24): the lookups (342 ms p50) followed by the config call (445 ms
 * p50) were ~790 ms of the ~1.5 s of a shopper's wait that is ours.
 *
 * Nothing about WHICH calculator a page gets changes. DetectPage still decides
 * pageurl → cart → product in the same order, and the three api functions it
 * and the page processors call — `getNonProductByPageUrl`,
 * `fetchCalculatorByProduct`, `LoadCalculator` / `LoadCalculatorNonProduct` —
 * keep their signatures: each first asks this module for the resolved answer
 * and only issues its own request when there is none. So:
 *
 *   - resolve answered 200  → zero further requests; the config functions hand
 *     back a synthetic `Response` carrying the payload (or a 204 when the id
 *     resolved but the calculator is not loadable — exactly what the old
 *     endpoint answered), so every call site behaves as before;
 *   - resolve answered 204  → no mapping, no assignment: pageurl and product
 *     lookups report "nothing" without a request; the theme-DOM fallback runs;
 *   - resolve unavailable (404 from an older API, network failure, anything
 *     unexpected) → every function falls through to its original request,
 *     retries included. Deploy order can never break a page.
 *
 * Preview mode is untouched: the merchant's editor loads through the old path
 * (`isPreview` is forwarded, but the preview entry never goes via DetectPage).
 *
 * DIV-037 — render from cache (#116 item 39). The last 200 answer for this
 * exact page (shop, path, product, locale) is kept in localStorage for a day.
 * On the next view the page renders from it at once — zero round-trips before
 * the calculator is on screen — while the network request still goes out and
 * REVALIDATES: a different payload replaces the entry for the next view, a 204
 * (calculator unassigned) deletes it. This view is NOT re-rendered live: the
 * render pipeline is not idempotent (it appends the loader, injects the
 * merchant's script and clones the Add-to-Cart button), so a live swap would
 * double all of that. The cost is that a merchant's change reaches a
 * returning shopper one page view late; `payloadSource()` reports whether a
 * view was served from cache and whether the fresh answer turned out to differ
 * (`cache-stale`), so that cost is measured, not assumed. Never for preview,
 * never for 204s, never when the payload is over MAX_BYTES or localStorage is
 * unavailable (private mode) — every storage access is wrapped.
 */
import { appState } from "../state/app-state";
import { shopperLocale } from "../i18n/translate";

export interface ResolvedCalculator {
  mode: "product" | "nonproduct";
  calculatorId: string;
  elementSelector: string | null;
  /** The `calculator/app` payload, or null = not loadable (the old 204). */
  calculator: any | null;
}

/** `none`: the API knows nothing for this page. `unavailable`: fall back to the old flow. */
type Resolution = ResolvedCalculator | "none" | "unavailable";

let pending: Promise<Resolution> | undefined;

/** Where this view's payload came from — for the attempt report. */
export type PayloadSource = "network" | "cache" | "cache-stale";
let source: PayloadSource = "network";
export function payloadSource(): PayloadSource {
  return source;
}

const CACHE_PREFIX = "cpc:resolve:v1:";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CACHE_MAX_ENTRIES = 30;
const CACHE_MAX_BYTES = 200_000;

interface CacheEntry {
  savedAt: number;
  resolved: ResolvedCalculator;
}

function cacheKey(): string | undefined {
  if (appState.cpc_isPreview) return undefined;
  const locale = shopperLocale() ?? "";
  return `${CACHE_PREFIX}${appState.cpc_shopUrl}|${window.location.origin}${window.location.pathname}|${appState.cpc_productId}|${locale}`;
}

function readCache(key: string): ResolvedCalculator | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as Partial<CacheEntry>;
    const r = entry.resolved;
    if (typeof entry.savedAt !== "number" || Date.now() - entry.savedAt > CACHE_TTL_MS) return undefined;
    if (!r || (r.mode !== "product" && r.mode !== "nonproduct") || typeof r.calculatorId !== "string" || !r.calculator) return undefined;
    return r;
  } catch {
    return undefined;
  }
}

function writeCache(key: string, resolved: ResolvedCalculator): void {
  try {
    const json = JSON.stringify({ savedAt: Date.now(), resolved } satisfies CacheEntry);
    if (json.length > CACHE_MAX_BYTES) return;
    pruneCache(key);
    window.localStorage.setItem(key, json);
  } catch {
    // Quota, private mode, disabled storage: the page simply is not cached.
  }
}

function removeCache(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Same as above.
  }
}

/** Keep at most CACHE_MAX_ENTRIES of ours (oldest out), so a store's catalogue cannot fill the origin's quota. */
function pruneCache(keep: string): void {
  const ours: Array<{ key: string; savedAt: number }> = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(CACHE_PREFIX) || key === keep) continue;
    let savedAt = 0;
    try {
      savedAt = (JSON.parse(window.localStorage.getItem(key) ?? "{}") as Partial<CacheEntry>).savedAt ?? 0;
    } catch {
      // unreadable → treated as oldest
    }
    ours.push({ key, savedAt });
  }
  ours.sort((a, b) => a.savedAt - b.savedAt);
  while (ours.length >= CACHE_MAX_ENTRIES) window.localStorage.removeItem(ours.shift()!.key);
}

/** The fresh network answer against what this view rendered from. */
function reconcile(key: string, cached: ResolvedCalculator, fresh: Resolution): void {
  if (fresh === "unavailable") return; // not an answer; the cache stands
  if (fresh === "none" || fresh.calculator === null) {
    removeCache(key);
  } else if (JSON.stringify(fresh) !== JSON.stringify(cached)) {
    writeCache(key, fresh);
  } else {
    return; // identical: the copy this view rendered from was current
  }
  source = "cache-stale";
  console.log("cpc-payload-cache", source);
}

function resolveUrl(): string {
  const locale = shopperLocale();
  return (
    appState.baseCalculatorNewApiUrl +
    "calculator/app/resolve?shop=" +
    appState.cpc_shopUrl +
    (appState.cpc_productId > 0 ? "&productId=" + appState.cpc_productId : "") +
    "&pageUrl=" +
    window.location.href +
    "&isPreview=" +
    appState.cpc_isPreview +
    (locale ? "&locale=" + locale : "")
  );
}

async function request(): Promise<Resolution> {
  try {
    const response = await fetch(resolveUrl());
    if (response.status === 204) return "none";
    if (!response.ok) return "unavailable";
    const body = await response.json();
    if (!body || (body.mode !== "product" && body.mode !== "nonproduct") || typeof body.calculatorId !== "string") {
      return "unavailable";
    }
    return {
      mode: body.mode,
      calculatorId: body.calculatorId,
      elementSelector: body.elementSelector ?? null,
      calculator: body.calculator ?? null,
    };
  } catch {
    // A rejected request (offline, blocked, aborted) is not an answer. The old
    // flow, with its own retry (DIV-022), takes over.
    return "unavailable";
  }
}

/**
 * Issue the request (once). Called first thing in DetectPage. With a cached
 * answer for this page the resolution is immediate and the request only
 * revalidates (DIV-037); without one the request IS the resolution, and its
 * 200 is cached for next time.
 */
export function prefetchResolve(): void {
  if (pending) return;
  const key = cacheKey();
  const cached = key ? readCache(key) : undefined;
  const network = request();
  if (key && cached) {
    source = "cache";
    console.log("cpc-payload-cache", source);
    pending = Promise.resolve(cached);
    network.then((fresh) => reconcile(key, cached, fresh));
  } else {
    pending = network.then((fresh) => {
      if (key) {
        if (fresh === "none") removeCache(key);
        else if (typeof fresh !== "string" && fresh.calculator !== null) writeCache(key, fresh);
      }
      return fresh;
    });
  }
}

/** The resolution, or `unavailable` when nothing was prefetched. */
export function resolution(): Promise<Resolution> {
  return pending ?? Promise.resolve("unavailable");
}

/**
 * A `Response` for the config functions to hand back in place of their own
 * request, or undefined when the old request must be made. Mirrors the old
 * endpoint exactly: 200 with the payload, or 204 when the calculator is not
 * loadable. Only for the calculator that was resolved — a different id (the
 * theme-DOM fallback picked one) still goes to the network.
 */
export async function resolvedConfigResponse(calculatorId: string): Promise<Response | undefined> {
  const r = await resolution();
  if (typeof r === "string" || r.calculatorId !== calculatorId) return undefined;
  if (r.calculator === null) return new Response(null, { status: 204 });
  return new Response(JSON.stringify(r.calculator), {
    status: 200,
    headers: { "Content-Type": "application/json" },
  });
}

/** Test/diagnostic aid. */
export function resetResolveForTests(): void {
  pending = undefined;
  source = "network";
}
